import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { ProcessStatusSchema, ProcessWithStepsSchema } from '../../entities/process';
import { ProcessStepAssignmentSchema } from '../../entities/process-step-assignment';
import { ProcessCapacitySchema } from '../../shared/capacity';
import { ProcessCapabilitySchema } from '../../shared/authorization';
import { OpenDraftAssignmentSchema } from '../process-draft/process-draft.schema';
import { EntitySuggestionSchema } from '../../entities/entity-suggestion/entity-suggestion.schema';
import { ProcessEventNodeSchema } from '../../entities/process-node';
import { ProcessNoteSchema } from '../../entities/process-note';
/**
 * Show Process Request Schema
 * GET /api/v2/process/:id
 * Process ID provided as URL parameter
 */ export const ShowProcessRequestSchema = z.void();
/**
 * Show Process Response Schema
 * Returned on successful retrieval
 * Contains the requested process object with optional process step recordings and pending assignments
 */ export const ShowProcessResponseSchema = z.object({
    process: ProcessWithStepsSchema,
    pendingAssignments: z.array(ProcessStepAssignmentSchema),
    /**
   * In-flight viral invitations on the published process. Rendered as
   * read-only placeholder nodes downstream of their source step (or START).
   * Unlike the draft editor, these are display-only — no click-through.
   */ openAssignments: z.array(OpenDraftAssignmentSchema),
    /**
   * EntitySuggestions referenced by the open-assignment placeholders (their
   * suggestion-typed `assignedTo` refs), of any status. Lets the flow builder
   * resolve a proposed Team/System placeholder to its name. Mirrors
   * `GetProcessDraftResponse.entitySuggestions`; required because a
   * START-anchored suggestion lives in no recording's `entitySuggestions[]`.
   */ entitySuggestions: z.array(EntitySuggestionSchema),
    /**
   * Event nodes of the process (ADR 0003). Optional during the rollout window
   * only — the backend always ships it since issue 07.
   */ events: z.array(ProcessEventNodeSchema).optional(),
    /**
   * Published sticky notes (`processnotes`), rendered read-only in the flow
   * viewer and analyzer. A note with empty `targets` is a free, process-wide
   * comment; otherwise it annotates the listed nodes by `stableStepId`.
   */ notes: z.array(ProcessNoteSchema),
    status: ProcessStatusSchema,
    /**
   * Applicable pending survey rows holding the process at `recording_complete`;
   * always 0 for any other status. `null` when the viewer lacks
   * ReadProcessInsights — the status itself is never redacted, being a verdict
   * rather than content.
   */ pendingSurveyCount: z.number().nullable(),
    /**
   * The one capacity computation, mirroring what the calculator returns.
   *
   * `null` means WITHHELD: the viewer has no `ReadSensitive`, so the whole
   * survey-derived aggregation was skipped. Non-null with `overall: null` and
   * empty maps means readable, but nothing was computable.
   */ capacity: ProcessCapacitySchema.nullable(),
    /**
   * The Prozessbewertung — the mean of the star ratings over this process's
   * steps, computed server-side (ADR 0021 decision 3). `null` for a viewer
   * without `canViewProcessInsights`, and `null` when nobody has rated a step.
   */ averageRating: z.number().nullable(),
    capabilities: ProcessCapabilitySchema
});
/**
 * Show Process Error Schema
 * Returned when retrieval fails
 */ export const ShowProcessErrorSchema = BaseErrorSchema;

//# sourceMappingURL=show-process.schema.js.map